import { Link, useLocation } from 'react-router-dom';
import { cn } from '../ui/cn';
import type { ModuleDef, ModuleRootTab } from './modulesConfig';
import { subModuleOwnsPath, subModulesForModule } from './modulesConfig';
import { NavMenuIconLabel } from './NavMenuIconLabel';
import { iconForSubModule } from './navIcons';
import { ScrollNavBar, taskNavListClassName } from './ScrollNavBar';

const tabClassName = (isActive: boolean) =>
  cn(
    'inline-flex min-h-11 items-center whitespace-nowrap border-b-2 px-3 py-2 text-sm no-underline transition-colors ease-ds hover:bg-muted',
    isActive
      ? 'border-primary font-semibold text-text-primary'
      : 'border-transparent font-normal text-text-secondary',
  );

/**
 * Sub-module tab strip for nested deep modules (ADR-014 Amendment 3).
 *
 * Two callers, one strip:
 * - **Mobile picker** (default `className="md:hidden"`): renders below tier-1 on
 *   `< md` when a nested module with tier-3 tasks is active — desktop uses the
 *   expanded `ModuleRail` nested list instead. The strip wraps (ADR-014 A18).
 * - **All-viewport tier-2 nav** (`className=""`, PRO-0600 follow-up): when a
 *   module's sub-modules are terminal (`subModulesAsTaskTabs`, e.g. functional
 *   Complaints), the strip *is* the tier-2 navigation on every breakpoint and the
 *   rail carries no nested list — the ATR task-bar pattern applied to sub-modules.
 */
export function ModuleSubModuleNav({
  module,
  complaintsEnabled = false,
  profileSettingsEnabled = false,
  rootTab = null,
  className = 'md:hidden',
}: {
  module: ModuleDef;
  complaintsEnabled?: boolean;
  /** PRO-0740 / ADR-014 amendment — drops the re-homed Referrals My Coverage / Service Profiles tabs. */
  profileSettingsEnabled?: boolean;
  /** Optional leading module-root "Dashboard" tab (Referrals home dashboard, ADR-038 A3). */
  rootTab?: ModuleRootTab | null;
  className?: string;
}) {
  const { pathname } = useLocation();
  const subModules = subModulesForModule(module, complaintsEnabled, profileSettingsEnabled);

  return (
    <ScrollNavBar aria-label={`${module.label} sub-modules`} className={className}>
      <ul className={cn(taskNavListClassName, 'py-2')}>
        {rootTab
          ? (() => {
              // The module-root tab is active only on an exact match — a prefix match
              // would light it up on every `/referrals/*` sub-module page.
              const isActive = pathname === rootTab.to;
              return (
                <li>
                  <Link
                    to={rootTab.to}
                    aria-current={isActive ? 'page' : undefined}
                    className={tabClassName(isActive)}
                  >
                    <NavMenuIconLabel
                      icon={iconForSubModule(rootTab.iconId)}
                      active={isActive}
                      className="gap-2"
                      iconClassName="h-4 w-4"
                    >
                      {rootTab.label}
                    </NavMenuIconLabel>
                  </Link>
                </li>
              );
            })()
          : null}
        {subModules.map((sub) => {
          const isActive = subModuleOwnsPath(sub, pathname, module.id, complaintsEnabled);
          return (
            <li key={sub.id}>
              <Link
                to={sub.basePath}
                aria-current={isActive ? 'page' : undefined}
                className={tabClassName(isActive)}
              >
                <NavMenuIconLabel
                  icon={iconForSubModule(sub.id)}
                  active={isActive}
                  className="gap-2"
                  iconClassName="h-4 w-4"
                >
                  {sub.label}
                </NavMenuIconLabel>
              </Link>
            </li>
          );
        })}
      </ul>
    </ScrollNavBar>
  );
}
